/**
 * Incremental opening updates — patch doors/windows in the live 3D scene without
 * rebuilding the whole room (used by the 2D plan editor).
 */

import { createSurfaceLibrary } from "./surface_library.js";
import { ensureBalconyViewWalls, renderWallMeshes } from "./wall_render.js";
import { wallLength } from "./wall_geometry.js";

const AXIS_EPS = 1e-3;

/** Match a plan-edge segment (world x/z) to the nearest built wall. */
export function matchWallForSegment(room, ax, az, bx, bz) {
  let best = null;
  let bestScore = Infinity;
  const elen = Math.hypot(bx - ax, bz - az) || 1;
  for (const w of room?.walls ?? []) {
    const wax = w.start[0];
    const waz = w.start[1];
    const wbx = w.end[0];
    const wbz = w.end[1];
    const wlen = Math.hypot(wbx - wax, wbz - waz) || 1;
    if (Math.abs(wlen - elen) > Math.max(0.35, wlen * 0.12)) continue;
    const d1 = Math.hypot(ax - wax, az - waz) + Math.hypot(bx - wbx, bz - wbz);
    const d2 = Math.hypot(ax - wbx, az - wbz) + Math.hypot(bx - wax, bz - waz);
    const score = Math.min(d1, d2);
    if (score < bestScore) {
      bestScore = score;
      best = w;
    }
  }
  return bestScore < 0.45 ? best : null;
}

function openingIdFromMeshName(name) {
  const n = String(name ?? "");
  if (n.startsWith("window-")) return n.slice("window-".length);
  if (n.startsWith("curtain-rod-")) return n.slice("curtain-rod-".length);
  if (n.startsWith("curtain-")) return n.slice("curtain-".length).replace(/-[lr]$/, "");
  return "";
}

/** Destroy window / curtain entities for the listed opening ids. */
export function destroyOpeningMeshes(roomRoot, openingIds = []) {
  if (!roomRoot || !openingIds.length) return;
  const ids = new Set(openingIds.map(String));
  const kill = [];
  for (const child of [...roomRoot.children]) {
    const wid = openingIdFromMeshName(child.name);
    if (wid && ids.has(wid)) kill.push(child);
  }
  for (const e of kill) e.destroy();
}

/** Drop window GLBs that no longer exist on the room (2D delete leftovers). */
export function clearOrphanOpeningMeshes(roomRoot, room) {
  if (!roomRoot) return;
  const live = new Set(
    (room?.openings ?? [])
      .filter((o) => o.type === "window" || o.type === "door")
      .map((o) => String(o.id)),
  );
  const kill = [];
  for (const child of [...roomRoot.children]) {
    const n = String(child.name ?? "");
    if (!n.startsWith("window-") && !n.startsWith("curtain-")) continue;
    const wid = openingIdFromMeshName(n);
    if (wid && !live.has(wid)) kill.push(child);
  }
  for (const e of kill) e.destroy();
}

/** Destroy wall segments + window panes tied to the given wall ids. */
export function clearWallMeshes(roomRoot, wallIds, openingIds = []) {
  const opSet = new Set((openingIds ?? []).map(String));
  const kill = [];
  for (const child of [...roomRoot.children]) {
    const n = child.name ?? "";
    if (wallIds.some((id) => n.startsWith(`wall-${id}-`))) kill.push(child);
    else if (n.startsWith("window-") || n.startsWith("curtain-")) {
      const wid = openingIdFromMeshName(n);
      if (opSet.has(wid)) kill.push(child);
    }
  }
  for (const e of kill) e.destroy();
}

/** Remove every wall segment, window pane, door leaf, and multi-storey shell (cleared apartment). */
export function stripStructureMeshes(roomRoot) {
  if (!roomRoot) return;
  const kill = [];
  for (const child of [...roomRoot.children]) {
    const n = child.name ?? "";
    if (
      n.startsWith("wall-") ||
      n.startsWith("window-") ||
      n.startsWith("door-") ||
      n.startsWith("shell-") ||
      n === "building-roof"
    ) {
      kill.push(child);
    } else if (n === "ceiling" && child.render) {
      child.render.enabled = false;
    }
  }
  for (const e of kill) e.destroy();
}

/**
 * Rebuild plaster + frames + glass for the listed walls only.
 * @param {pc.AppBase} app
 * @param {object} room
 * @param {pc.Entity} roomRoot
 * @param {string[]} wallIds
 * @param {object} [shared]
 */
export function rebuildWallsForIds(app, room, roomRoot, wallIds, shared = {}) {
  if (!roomRoot || !wallIds?.length) return;
  const ids = [...new Set(wallIds.filter(Boolean))];
  // Reuse the room's shared surface library (one material per surface type per
  // build). Fall back to a fresh library with the same room-derived options the
  // full build uses, so tiling/sky can never drift between paths.
  const b = room.bounds;
  const surfOpts = {
    width: room.dimensions?.width ?? (b ? b.max[0] - b.min[0] : 6),
    depth: room.dimensions?.depth ?? (b ? b.max[2] - b.min[2] : 6),
    height: room.dimensions?.height ?? 2.8,
    skyTexture: shared.skyTexture ?? null,
    finishes: room.finishes ?? {},
  };
  let surf = roomRoot._aspectSurf ?? createSurfaceLibrary(app, surfOpts);
  if (room.walls?.some((w) => w.view) && (!surf.railMetal || !surf.copingStone)) {
    surf = createSurfaceLibrary(app, surfOpts);
    roomRoot._aspectSurf = surf;
  }

  ensureBalconyViewWalls(room);
  const affectedOps = (room.openings ?? []).filter((o) => ids.includes(o.wall)).map((o) => o.id);
  clearWallMeshes(roomRoot, ids, affectedOps);
  // 2D deletes remove the record first — leftover window GLBs are not in affectedOps.
  clearOrphanOpeningMeshes(roomRoot, room);
  // View / balcony walls must never keep a window mesh on the rail.
  for (const w of room.walls ?? []) {
    if (!ids.includes(w.id) || !w.view) continue;
    const stale = (room.openings ?? []).filter((o) => o.wall === w.id && o.type === "window").map((o) => o.id);
    if (stale.length) destroyOpeningMeshes(roomRoot, stale);
  }

  for (const w of room.walls ?? []) {
    if (!ids.includes(w.id)) continue;
    if (wallLength(w) < AXIS_EPS) continue;
    renderWallMeshes(app, room, roomRoot, w, surf, shared);
  }
}
